<template>
  <div class="ppv-tiers-container">
    <!-- PPV Configuration -->
    <div class="config-section">
      <h3 class="section-title">PPV Settings</h3>
      <div class="config-card">
        <div class="config-row">
          <div class="config-item">
            <label class="toggle-label" @click="ppvConfig.ppv_enabled = !ppvConfig.ppv_enabled; saveConfig()">
              <div :class="['toggle', ppvConfig.ppv_enabled && 'on']">
                <div class="toggle-knob"></div>
              </div>
              <span>PPV Enabled</span>
            </label>
            <p class="config-hint">Allow users to purchase pay-per-view content</p>
          </div>
          <div class="config-item">
            <label class="toggle-label" @click="ppvConfig.ppv_enable_by_default = !ppvConfig.ppv_enable_by_default; saveConfig()">
              <div :class="['toggle', ppvConfig.ppv_enable_by_default && 'on']">
                <div class="toggle-knob"></div>
              </div>
              <span>Enable by Default</span>
            </label>
            <p class="config-hint">New items have PPV enabled automatically</p>
          </div>
          <div class="config-item">
            <label class="config-label">Watching Period</label>
            <div class="days-input-wrapper">
              <input
                type="number"
                v-model.number="ppvConfig.ppv_watching_days"
                @change="saveConfig"
                min="1"
                max="365"
                class="days-input"
              />
              <span class="days-suffix">days</span>
            </div>
            <p class="config-hint">How long users can watch after purchase</p>
          </div>
          <div class="config-item">
            <label class="config-label">Supported Types</label>
            <div class="type-toggles">
              <label
                class="type-chip"
                :class="{ active: ppvConfig.ppv_types.includes('video') }"
                @click="togglePpvType('video')"
              >Video</label>
              <label
                class="type-chip"
                :class="{ active: ppvConfig.ppv_types.includes('episode') }"
                @click="togglePpvType('episode')"
              >Episode</label>
            </div>
            <p class="config-hint">Media types that support PPV</p>
          </div>
        </div>
        <transition name="fade">
          <div v-if="configSaving" class="config-status saving">Saving...</div>
          <div v-else-if="configSaved" class="config-status saved">Saved</div>
        </transition>
      </div>
    </div>

    <!-- Section divider -->
    <h3 class="section-title">Price Tiers</h3>

    <!-- Header with search and add -->
    <div class="ppv-header">
      <div class="search-box">
        <input
          v-model="filter"
          type="text"
          placeholder="Search tiers..."
          class="search-input"
        />
      </div>
      <div class="header-actions">
        <div class="filter-pills">
          <button
            :class="['pill', statusFilter === '*' && 'active']"
            @click="statusFilter = '*'"
          >All</button>
          <button
            :class="['pill', statusFilter === 'active' && 'active']"
            @click="statusFilter = 'active'"
          >Active</button>
          <button
            :class="['pill', statusFilter === 'inactive' && 'active']"
            @click="statusFilter = 'inactive'"
          >Inactive</button>
        </div>
        <button class="add-btn" @click="startCreate">+ New Tier</button>
      </div>
    </div>

    <!-- Tiers List -->
    <div class="tiers-list">
      <!-- New Tier Form -->
      <transition name="slide-fade">
        <div v-if="isCreating && newTier" class="tier-card editing">
          <div class="card-row">
            <div class="card-main">
              <input
                v-model="newTier.name"
                type="text"
                placeholder="Tier name..."
                class="name-input"
                ref="newTierName"
                @keyup.enter="saveNewTier"
              />
              <div class="card-toggles">
                <label class="toggle-label" @click="newTier.is_active = !newTier.is_active">
                  <div :class="['toggle', newTier.is_active && 'on']">
                    <div class="toggle-knob"></div>
                  </div>
                  <span>Active</span>
                </label>
                <label class="toggle-label" @click="newTier.is_default = !newTier.is_default">
                  <div :class="['toggle', newTier.is_default && 'on']">
                    <div class="toggle-knob"></div>
                  </div>
                  <span>Default</span>
                </label>
              </div>
            </div>
            <div class="card-prices">
              <div v-for="currency in currencies" :key="currency" class="price-field">
                <label>{{ currency }}</label>
                <input
                  type="number"
                  step="0.01"
                  :value="(newTier.prices[currency] || 0) / 100"
                  @input="$set(newTier.prices, currency, Math.round($event.target.value * 100))"
                  @keyup.enter="saveNewTier"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div class="card-actions">
              <button class="btn-cancel" @click="cancelCreate">Cancel</button>
              <button class="btn-save" @click="saveNewTier">Create</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Existing Tiers -->
      <transition-group name="list" tag="div">
        <div
          v-for="tier in filteredTiers"
          :key="tier.id"
          :class="['tier-card', editingId === tier.id ? 'editing' : 'viewable']"
          @click="editingId !== tier.id && startEdit(tier)"
        >
          <!-- View Mode -->
          <div v-if="editingId !== tier.id" class="card-row">
            <div class="card-main">
              <div class="tier-name">
                {{ tier.name }}
                <span v-if="tier.is_default" class="badge default">Default</span>
                <span :class="['badge', tier.is_active ? 'active' : 'inactive']">
                  {{ tier.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="tier-meta">
                <span v-if="tier.updated_at">Updated {{ formatDate(tier.updated_at) }}</span>
              </div>
            </div>
            <div class="card-prices">
              <div v-for="currency in currencies" :key="currency" class="price-display">
                <span class="currency">{{ currency }}</span>
                <span class="amount">{{ formatPrice(tier.prices[currency] || 0) }}</span>
              </div>
            </div>
            <div class="card-actions">
              <button class="btn-delete" @click.stop="confirmDelete(tier)">Delete</button>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-else class="card-row" @click.stop>
            <div class="card-main">
              <input
                v-model="editingTier.name"
                type="text"
                class="name-input"
                ref="editTierName"
                @keyup.enter="saveEdit"
              />
              <div class="card-toggles">
                <label class="toggle-label" @click="editingTier.is_active = !editingTier.is_active">
                  <div :class="['toggle', editingTier.is_active && 'on']">
                    <div class="toggle-knob"></div>
                  </div>
                  <span>Active</span>
                </label>
                <label class="toggle-label" @click="editingTier.is_default = !editingTier.is_default">
                  <div :class="['toggle', editingTier.is_default && 'on']">
                    <div class="toggle-knob"></div>
                  </div>
                  <span>Default</span>
                </label>
              </div>
            </div>
            <div class="card-prices">
              <div v-for="currency in currencies" :key="currency" class="price-field">
                <label>{{ currency }}</label>
                <input
                  type="number"
                  step="0.01"
                  :value="(editingTier.prices[currency] || 0) / 100"
                  @input="$set(editingTier.prices, currency, Math.round($event.target.value * 100))"
                  @keyup.enter="saveEdit"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div class="card-actions">
              <button class="btn-cancel" @click.stop="cancelEdit">Cancel</button>
              <button class="btn-save" @click.stop="saveEdit">Save</button>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Empty State -->
      <div v-if="!isCreating && filteredTiers.length === 0" class="empty-state">
        <p v-if="filter || statusFilter !== '*'">No tiers match your filters</p>
        <p v-else>No PPV tiers yet</p>
        <button class="add-btn" @click="startCreate">+ Create First Tier</button>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <transition name="modal-fade">
      <div v-if="deletingTier" class="modal-overlay" @click.self="deletingTier = null">
        <div class="confirm-modal">
          <h3>Delete Tier</h3>
          <p>Are you sure you want to delete <strong>{{ deletingTier.name }}</strong>?</p>
          <p class="warning">This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="deletingTier = null">Cancel</button>
            <button class="btn-delete" @click="deleteTier">Delete</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Low Price Confirmation -->
    <transition name="modal-fade">
      <div v-if="lowPriceConfirm" class="modal-overlay" @click.self="lowPriceConfirm = null">
        <div class="confirm-modal">
          <h3>Confirm Low Price</h3>
          <p>Some prices are under 0.50. Is this correct?</p>
          <p class="warning">Are you sure you want to save?</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="lowPriceConfirm = null">Cancel</button>
            <button class="btn-save" @click="confirmLowPriceSave">Yes, Save</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // PPV Config
      ppvConfig: {
        ppv_enabled: true,
        ppv_enable_by_default: true,
        ppv_types: ['video', 'episode'],
        ppv_watching_days: 7
      },
      configSaving: false,
      configSaved: false,
      configSaveTimeout: null,

      // Currencies (loaded from config API)
      currencies: ['EUR', 'USD', 'GBP'],

      // Tiers
      tiers: [],
      filter: '',
      statusFilter: '*',

      // Create mode
      isCreating: false,
      newTier: null,

      // Edit mode
      editingId: null,
      editingTier: null,

      // Delete confirmation
      deletingTier: null,

      // Low price confirmation
      lowPriceConfirm: null, // 'create' or 'edit'

      // Decimal warning
      showDecimalWarning: false,
      decimalWarningTimeout: null
    }
  },

  computed: {
    filteredTiers() {
      return this.tiers.filter(tier => {
        if (this.statusFilter === 'active' && !tier.is_active) return false
        if (this.statusFilter === 'inactive' && tier.is_active) return false
        if (this.filter && !tier.name.toLowerCase().includes(this.filter.toLowerCase())) return false
        return true
      })
    }
  },

  methods: {
    // Config methods
    loadConfig() {
      fetch('/admin/api/v2/config/ppv')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null // Config doesn't exist yet
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && resp.data.value) {
            this.ppvConfig = {
              ppv_enabled: resp.data.value.ppv_enabled ?? true,
              ppv_enable_by_default: resp.data.value.ppv_enable_by_default ?? true,
              ppv_types: resp.data.value.ppv_types ?? ['video', 'episode'],
              ppv_watching_days: resp.data.value.ppv_watching_days ?? 7
            }
          }
        })
        .catch(() => {
          // Silently use defaults if config doesn't exist
        })
    },

    loadCurrencies() {
      fetch('/admin/api/v2/config/currencies')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && Array.isArray(resp.data.value)) {
            this.currencies = resp.data.value
          }
        })
        .catch(() => {
          // Silently use defaults if config doesn't exist
        })
    },

    saveConfig() {
      if (this.configSaveTimeout) {
        clearTimeout(this.configSaveTimeout)
      }

      this.configSaving = true
      this.configSaved = false

      fetch('/admin/api/v2/config/ppv', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            value: this.ppvConfig,
            is_public: true
          }
        })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.configSaving = false
          this.configSaved = true
          this.configSaveTimeout = setTimeout(() => {
            this.configSaved = false
          }, 2000)
        })
        .catch(() => {
          this.configSaving = false
          this.$root.app.showAlert('Error saving PPV config', 'danger')
        })
    },

    togglePpvType(type) {
      const idx = this.ppvConfig.ppv_types.indexOf(type)
      if (idx >= 0) {
        this.ppvConfig.ppv_types.splice(idx, 1)
      } else {
        this.ppvConfig.ppv_types.push(type)
      }
      this.saveConfig()
    },

    // Tier methods
    emptyTier() {
      const prices = {}
      this.currencies.forEach(c => prices[c] = 0)
      return {
        name: '',
        prices,
        is_active: true,
        is_default: false
      }
    },

    loadTiers() {
      fetch('/admin/api/v2/ppv-price-tiers')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.tiers = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading PPV tiers', 'danger')
        })
    },

    formatPrice(cents) {
      return (cents / 100).toFixed(2)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    // Create
    startCreate() {
      this.cancelEdit()
      this.isCreating = true
      this.newTier = this.emptyTier()
      this.$nextTick(() => {
        if (this.$refs.newTierName) this.$refs.newTierName.focus()
      })
    },

    cancelCreate() {
      this.isCreating = false
      this.newTier = this.emptyTier()
    },

    saveNewTier(skipLowPriceCheck = false) {
      if (!this.newTier.name.trim()) {
        this.$root.app.showAlert('Please enter a tier name', 'warning')
        return
      }

      if (!skipLowPriceCheck && this.hasLowPrice(this.newTier.prices)) {
        this.lowPriceConfirm = 'create'
        return
      }

      fetch('/admin/api/v2/ppv-price-tiers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.newTier })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Tier created successfully', 'success')
          this.cancelCreate()
          this.loadTiers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error creating tier', 'danger')
        })
    },

    // Edit
    startEdit(tier) {
      this.cancelCreate()
      this.editingId = tier.id
      // Ensure all currencies exist in the prices object
      const prices = { ...tier.prices }
      this.currencies.forEach(c => {
        if (prices[c] === undefined) prices[c] = 0
      })
      this.editingTier = { ...tier, prices }
    },

    cancelEdit() {
      this.editingId = null
      this.editingTier = null
    },

    saveEdit(skipLowPriceCheck = false) {
      if (!this.editingTier.name.trim()) {
        this.$root.app.showAlert('Please enter a tier name', 'warning')
        return
      }

      if (!skipLowPriceCheck && this.hasLowPrice(this.editingTier.prices)) {
        this.lowPriceConfirm = 'edit'
        return
      }

      fetch(`/admin/api/v2/ppv-price-tiers/${this.editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.editingTier })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Tier updated successfully', 'success')
          this.cancelEdit()
          this.loadTiers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error updating tier', 'danger')
        })
    },

    // Decimal check
    checkDecimalInput(e) {
      if (e.key === '.' || e.key === ',') {
        e.preventDefault()
        this.showDecimalWarning = true
        if (this.decimalWarningTimeout) {
          clearTimeout(this.decimalWarningTimeout)
        }
        this.decimalWarningTimeout = setTimeout(() => {
          this.showDecimalWarning = false
        }, 4000)
      }
    },

    // Low price check
    hasLowPrice(prices) {
      return Object.values(prices).some(p => p > 0 && p < 50)
    },

    confirmLowPriceSave() {
      const action = this.lowPriceConfirm
      this.lowPriceConfirm = null
      if (action === 'create') {
        this.saveNewTier(true)
      } else if (action === 'edit') {
        this.saveEdit(true)
      }
    },

    // Delete
    confirmDelete(tier) {
      this.deletingTier = tier
    },

    deleteTier() {
      fetch(`/admin/api/v2/ppv-price-tiers/${this.deletingTier.id}`, { method: 'DELETE' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Tier deleted successfully', 'success')
          this.deletingTier = null
          this.loadTiers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deleting tier', 'danger')
        })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('PPV Tiers', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.newTier = this.emptyTier()
    this.loadConfig()
    this.loadCurrencies()
    this.loadTiers()
  }
}
</script>

<style scoped>
.ppv-tiers-container {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Section titles */
.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

/* Config Section */
.config-section {
  margin-bottom: 2rem;
}

.config-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.config-row {
  display: flex;
  gap: 3rem;
  flex-wrap: wrap;
}

.config-item {
  min-width: 200px;
}

.config-label {
  font-size: 0.85rem;
  color: #E2E8F0;
  margin-bottom: 0.5rem;
  display: block;
}

.config-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.4rem;
}

.days-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.days-input {
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  text-align: center;
  transition: all 0.2s;
}

.days-input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(255, 255, 255, 0.1);
}

.days-suffix {
  color: #94A3B8;
  font-size: 0.85rem;
}

.type-toggles {
  display: flex;
  gap: 0.5rem;
}

.type-chip {
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #94A3B8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  user-select: none;
}

.type-chip:hover {
  border-color: #20B4FF;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.type-chip.active {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 4px rgba(32, 180, 255, 0.2);
}

.config-status {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.config-status.saving {
  color: #9ca3af;
}

.config-status.saved {
  color: #28a745;
  background: #28a74520;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Header */
.ppv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  max-width: 280px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 2px rgba(32, 180, 255, 0.2);
}

.search-input::placeholder {
  color: #94A3B8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-pills {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.pill {
  padding: 0.35rem 0.75rem;
  border: none;
  background: transparent;
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.pill:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.pill.active {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.add-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3);
}

/* Tiers List */
.tiers-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Card */
.tier-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.tier-card.viewable {
  cursor: pointer;
}

.tier-card.viewable:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
  cursor: pointer;
}

.tier-card.editing {
  background: rgba(255, 255, 255, 0.08);
  border-color: #20B4FF;
  box-shadow: 0 0 0 1px #20B4FF, 0 10px 25px rgba(0, 0, 0, 0.2);
}

.card-row {
  display: flex;
  padding: 1.25rem 1.5rem;
  align-items: center;
  gap: 2rem;
}

/* Card Main */
.card-main {
  flex: 1;
  min-width: 250px;
}

.tier-name {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.default {
  background: rgba(32, 180, 255, 0.15);
  color: #20B4FF;
  border: 1px solid rgba(32, 180, 255, 0.2);
}

.badge.active {
  background: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.badge.inactive {
  background: rgba(108, 117, 125, 0.15);
  color: #94A3B8;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.tier-meta {
  font-size: 0.8rem;
  color: #94A3B8;
  margin-top: 0.25rem;
}

.name-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
}

.name-input:focus {
  outline: none;
  border-color: #20B4FF;
}

/* Toggles */
.card-toggles {
  display: flex;
  gap: 1.5rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.toggle-label span {
  font-size: 0.85rem;
  color: #E2E8F0;
}

.toggle {
  width: 36px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  position: relative;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.toggle.on {
  background: #20B4FF;
  border-color: #20B4FF;
}

.toggle-knob {
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 1px;
  left: 1px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.toggle.on .toggle-knob {
  left: 17px;
}

/* Prices */
.card-prices {
  flex: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}

.price-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.price-display .currency {
  font-size: 0.8rem;
  color: #94A3B8;
  font-weight: 600;
}

.price-display .amount {
  font-size: 0.9rem;
  color: #fff;
  font-family: monospace;
}

.price-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.price-field label {
  font-size: 0.75rem;
  color: #94A3B8;
  font-weight: 600;
}

.price-field input {
  width: 80px;
  padding: 0.4rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.price-field input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(0, 0, 0, 0.3);
}

.prices-hint-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 0.75rem;
}

.prices-hint {
  color: #94A3B8;
}

.decimal-warning {
  color: #ffc107;
  font-weight: 500;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Actions */
.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-save, .btn-cancel, .btn-delete {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-save {
  background: #28a745;
  color: #fff;
}

.btn-save:hover {
  background: #218838;
}

.btn-cancel {
  background: #3a3f47;
  color: #c9d1d9;
}

.btn-cancel:hover {
  background: #4a5058;
  color: #fff;
}

.btn-delete {
  background: transparent;
  color: #adb5bd;
}

.btn-delete:hover {
  background: #dc3545;
  color: #fff;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.empty-state p {
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal {
  background: #2a2e35;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 360px;
  width: 90%;
  border: 1px solid #3a3f47;
}

.confirm-modal h3 {
  margin: 0 0 0.75rem;
  color: #fff;
  font-size: 1.1rem;
}

.confirm-modal p {
  color: #c9d1d9;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.confirm-modal .warning {
  color: #dc3545;
  font-size: 0.8rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
}

.list-move {
  transition: transform 0.2s ease;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .ppv-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .header-actions {
    justify-content: space-between;
  }

  .card-row {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .card-prices {
    justify-content: flex-start;
  }

  .card-actions {
    justify-content: flex-end;
  }
}
</style>
